// Volumetric cloud layer (after Schneider, "Nubis"), noise generation and cloud shadow projection.

export const noiseGenGLSL = /* glsl */ `
uniform float uSlice;      // z slice index
uniform float uSize;       // volume resolution
uniform int uMode;         // 0 = base (perlin-worley + worley fbm), 1 = detail worley fbm
in vec2 vUv;
out vec4 fragColor;

vec3 hash33(vec3 p) {
  p = vec3(dot(p, vec3(127.1, 311.7, 74.7)), dot(p, vec3(269.5, 183.3, 246.1)), dot(p, vec3(113.5, 271.9, 124.6)));
  return fract(sin(p) * 43758.5453123);
}
float worley(vec3 p, float period) {
  vec3 id = floor(p);
  vec3 f = fract(p);
  float md = 1.0;
  for (int z = -1; z <= 1; z++) for (int y = -1; y <= 1; y++) for (int x = -1; x <= 1; x++) {
    vec3 o = vec3(x, y, z);
    vec3 cid = mod(id + o, period);
    vec3 pt = o + hash33(cid) - f;
    md = min(md, dot(pt, pt));
  }
  return 1.0 - sqrt(md);
}
float gradNoise(vec3 p, float period) {
  vec3 i = floor(p);
  vec3 f = fract(p);
  vec3 u = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);
  float n = 0.0;
  float res[8];
  for (int k = 0; k < 8; k++) {
    vec3 o = vec3(k & 1, (k >> 1) & 1, (k >> 2) & 1);
    vec3 g = hash33(mod(i + o, period)) * 2.0 - 1.0;
    res[k] = dot(normalize(g + 1e-4), f - o);
  }
  float x00 = mix(res[0], res[1], u.x), x10 = mix(res[2], res[3], u.x);
  float x01 = mix(res[4], res[5], u.x), x11 = mix(res[6], res[7], u.x);
  n = mix(mix(x00, x10, u.y), mix(x01, x11, u.y), u.z);
  return n;
}
float perlinFbm(vec3 p, float period, int oct) {
  float s = 0.0, a = 1.0, w = 0.0;
  for (int i = 0; i < 6; i++) {
    if (i >= oct) break;
    s += a * gradNoise(p, period);
    w += a; a *= 0.5; p *= 2.0; period *= 2.0;
  }
  return s / w;
}
float worleyFbm(vec3 p, float period) {
  return worley(p, period) * 0.625 + worley(p * 2.0, period * 2.0) * 0.25 + worley(p * 4.0, period * 4.0) * 0.125;
}
float remap(float v, float l0, float h0, float l1, float h1) { return l1 + (v - l0) * (h1 - l1) / (h0 - l0); }
void main() {
  vec3 uvw = vec3(vUv, (uSlice + 0.5) / uSize);
  if (uMode == 0) {
    float freq = 4.0;
    float pfbm = mix(1.0, perlinFbm(uvw * freq, freq, 6) * 0.5 + 0.5, 1.0);
    pfbm = abs(pfbm * 2.0 - 1.0);
    pfbm = 1.0 - pfbm;
    float w1 = worleyFbm(uvw * freq, freq);
    float w2 = worleyFbm(uvw * freq * 2.0, freq * 2.0);
    float w3 = worleyFbm(uvw * freq * 4.0, freq * 4.0);
    float pw = remap(pfbm, 0.0, 1.0, w1, 1.0);
    fragColor = vec4(pw, w1, w2, w3);
  } else {
    float freq = 4.0;
    fragColor = vec4(worleyFbm(uvw * freq, freq), worleyFbm(uvw * freq * 2.0, freq * 2.0), worleyFbm(uvw * freq * 4.0, freq * 4.0), 1.0);
  }
}
`;

export const cloudCommonGLSL = /* glsl */ `
uniform sampler3D uNoiseBase;
uniform sampler3D uNoiseDetail;
uniform sampler2D uWeatherMap;
uniform vec4 uCloudLayer;    // x base alt (m), y top alt (m), z coverage, w density
uniform vec4 uCloudShape;    // x type (0 stratus .. 1 cumulus .. 2 cumulonimbus), y detail erosion, z darkness, w anvil
uniform vec3 uWindOffset;    // accumulated wind displacement (m)
uniform float uWeatherScale; // meters per weather tile

float cremap(float v, float l0, float h0, float l1, float h1) { return l1 + (v - l0) * (h1 - l1) / (h0 - l0); }

float heightGradient(float h, float type) {
  float stratus = smoothstep(0.0, 0.1, h) * (1.0 - smoothstep(0.2, 0.35, h));
  float cumulus = smoothstep(0.0, 0.15, h) * (1.0 - smoothstep(0.35, 0.8, h));
  float cb = smoothstep(0.0, 0.08, h) * (1.0 - smoothstep(0.8, 1.0, h));
  return type < 1.0 ? mix(stratus, cumulus, type) : mix(cumulus, cb, type - 1.0);
}

float cloudDensity(vec3 p, float hf, bool detail) {
  if (hf < 0.0 || hf > 1.0) return 0.0;
  vec3 wp = p + uWindOffset;
  // skew with height (wind shear)
  wp.xz += hf * vec2(400.0, 120.0);
  vec2 wuv = wp.xz / uWeatherScale;
  vec4 wm = texture(uWeatherMap, wuv);
  float cov = uCloudLayer.z;
  float wcov = clamp(cremap(wm.r * 0.75 + wm.g * 0.25, 1.0 - cov, 1.0, 0.0, 1.0), 0.0, 1.0);
  wcov = max(wcov, smoothstep(0.75, 1.0, cov) * 0.9);
  if (wcov <= 0.001) return 0.0;
  float type = uCloudShape.x + (wm.b - 0.5) * 0.4 * step(0.5, uCloudShape.x);
  float hg = heightGradient(hf, clamp(type, 0.0, 2.0));
  // anvil spread near the top of storm clouds
  wcov = pow(wcov, cremap(clamp(1.0 - hf, 0.0, 1.0), 0.7, 0.8, 1.0, mix(1.0, 0.5, uCloudShape.w)));
  vec4 n = texture(uNoiseBase, wp / 5200.0);
  float lowFbm = n.g * 0.625 + n.b * 0.25 + n.a * 0.125;
  float base = cremap(n.r, -(1.0 - lowFbm), 1.0, 0.0, 1.0);
  base *= hg;
  float c = cremap(base, 1.0 - wcov, 1.0, 0.0, 1.0) * wcov;
  if (c <= 0.0) return 0.0;
  if (detail) {
    vec3 dn = texture(uNoiseDetail, wp / 900.0 + vec3(0.0, uWindOffset.x * 0.00002, 0.0)).rgb;
    float hfFbm = dn.r * 0.625 + dn.g * 0.25 + dn.b * 0.125;
    float mod = mix(hfFbm, 1.0 - hfFbm, clamp(hf * 6.0, 0.0, 1.0));
    c = cremap(c, mod * uCloudShape.y, 1.0, 0.0, 1.0);
  }
  return max(c, 0.0) * uCloudLayer.w;
}

// ray / spherical shell intersection, origin given relative to Earth center
vec2 raySphere(vec3 ro, vec3 rd, float r) {
  float b = dot(ro, rd);
  float c = dot(ro, ro) - r * r;
  float d = b * b - c;
  if (d < 0.0) return vec2(-1.0);
  float s = sqrt(d);
  return vec2(-b - s, -b + s);
}
`;

export const cloudMarchFrag = /* glsl */ `
uniform sampler2D uSceneDepth;
uniform sampler2D uTransLUT;
uniform sampler2D uSkyIrr;
uniform mat4 uInvProjView;
uniform vec3 uCamPos;
uniform vec3 uSunDirW;
uniform vec3 uSunE;
uniform vec3 uMoonDirW;
uniform vec3 uMoonE;
uniform float uSteps;
uniform float uFrame;
uniform float uLightning;
uniform vec3 uLightningPos;
in vec2 vUv;
out vec4 fragColor;

const float RE = 6371000.0;
float hg(float g, float c) { float g2 = g * g; return (1.0 - g2) / (4.0 * PI * pow(max(1.0 + g2 - 2.0 * g * c, 1e-4), 1.5)); }

vec3 transAlt(float alt, vec3 dir) {
  vec3 pMM = vec3(0.0, uGroundRadiusMM + max(alt, 0.0) * 1e-6, 0.0);
  return lutTransmittance(uTransLUT, pMM, dir) * smoothstep(-0.03, 0.02, dir.y);
}

void main() {
  float d = texture(uSceneDepth, vUv).r;
  vec4 fp = uInvProjView * vec4(vUv * 2.0 - 1.0, 1.0, 1.0);
  vec3 rd = normalize(fp.xyz / fp.w - uCamPos);
  float tScene = 1e9;
  if (d < 1.0) {
    vec4 wp = uInvProjView * vec4(vUv * 2.0 - 1.0, d * 2.0 - 1.0, 1.0);
    tScene = length(wp.xyz / wp.w - uCamPos);
  }
  vec3 ro = vec3(0.0, RE + uCamPos.y, 0.0);
  float rb = RE + uCloudLayer.x, rt = RE + uCloudLayer.y;
  vec2 ib = raySphere(ro, rd, rb), it = raySphere(ro, rd, rt);
  float alt = uCamPos.y;
  float t0, t1;
  if (alt < uCloudLayer.x) {
    if (ib.y < 0.0) { fragColor = vec4(0, 0, 0, 1); return; }
    // below the layer: looking down never reaches it (ground first)
    vec2 ig = raySphere(ro, rd, RE + 1200.0);
    if (ig.x > 0.0 && rd.y < 0.0) { fragColor = vec4(0, 0, 0, 1); return; }
    t0 = ib.y; t1 = it.y;
  } else if (alt > uCloudLayer.y) {
    if (it.x < 0.0) { fragColor = vec4(0, 0, 0, 1); return; }
    t0 = it.x; t1 = ib.x > 0.0 ? ib.x : it.y;
  } else {
    t0 = 0.0;
    t1 = ib.x > 0.0 ? ib.x : it.y;
  }
  t1 = min(t1, min(tScene, mix(90000.0, 220000.0, smoothstep(0.6, 0.95, uCloudLayer.z))));
  if (t1 <= t0) { fragColor = vec4(0, 0, 0, 1); return; }

  float steps = uSteps;
  float dt = (t1 - t0) / steps;
  dt = clamp(dt, 30.0, 1500.0);
  float jitter = fract(hash12(gl_FragCoord.xy) + uFrame * 0.618034);
  float t = t0 + dt * jitter;

  // light & ambient
  bool moon = uSunDirW.y < -0.06 && uMoonDirW.y > 0.0;
  vec3 Ldir = moon ? uMoonDirW : uSunDirW;
  vec3 LE = moon ? uMoonE : uSunE;
  float cosT = dot(rd, Ldir);
  float phase = mix(hg(0.75, cosT), hg(-0.2, cosT), 0.3) * 0.85 + hg(0.0, cosT) * 0.15;
  vec3 skyAmb = texelFetch(uSkyIrr, ivec2(0, 0), 0).rgb * 0.282095 * PI * 1.6;
  vec3 gndAmb = texelFetch(uSkyIrr, ivec2(10, 0), 0).rgb * vec3(0.42, 0.37, 0.33) * 0.9;
  float sigma = 0.045;
  vec3 L = vec3(0.0);
  float T = 1.0;
  float sumT = 0.0, sumW = 0.0;
  for (int i = 0; i < 160; i++) {
    if (float(i) >= steps || t > t1 || T < 0.015) break;
    vec3 p = ro + rd * t;
    float h = length(p) - RE;
    float hf = (h - uCloudLayer.x) / (uCloudLayer.y - uCloudLayer.x);
    vec3 wpos = vec3(uCamPos.x + rd.x * t, h, uCamPos.z + rd.z * t);
    float den = cloudDensity(wpos, hf, true);
    if (den > 0.002) {
      // light march toward the light
      float od = 0.0;
      float ls = 60.0;
      vec3 lp = wpos;
      for (int j = 0; j < 6; j++) {
        lp += Ldir * ls;
        float lhf = (lp.y - uCloudLayer.x) / (uCloudLayer.y - uCloudLayer.x);
        od += cloudDensity(lp, lhf, j < 3) * ls;
        ls *= 1.9;
      }
      // multiple-scattering approximation (Wrenninge octaves): each octave scatters with
      // less extinction and a more isotropic phase; scaled to approximate cloud albedo ~0.9
      float ms = 0.0;
      float a = 1.0, b = 1.0, c = 1.0;
      for (int o = 0; o < 4; o++) {
        float ph = mix(hg(0.78 * c, cosT), hg(-0.25 * c, cosT), 0.3);
        ms += a * ph * exp(-od * sigma * b);
        a *= 0.55; b *= 0.4; c *= 0.5;
      }
      float powder = 1.0 - exp(-den * sigma * 180.0);
      vec3 sunL = LE * transAlt(h, Ldir) * ms * 3.2 * mix(0.6, 1.0, powder) * (1.0 - uCloudShape.z * 0.65);
      vec3 amb = mix(gndAmb, skyAmb, clamp(hf * 1.1 + 0.25, 0.0, 1.0)) * (1.0 - uCloudShape.z * 0.7) * (0.6 + 0.4 * powder);
      vec3 flash = vec3(0.0);
      if (uLightning > 0.0) {
        float ld = length(wpos - uLightningPos);
        flash = vec3(0.9, 0.92, 1.0) * uLightning * 60.0 * exp(-ld / 2500.0);
      }
      vec3 S = (sunL + amb * INV_PI * 2.4 + flash) * den * sigma;
      float stepT = exp(-den * sigma * dt);
      L += T * (S - S * stepT) / max(den * sigma, 1e-6);
      T *= stepT;
      sumT += t * (1.0 - stepT) * T; sumW += (1.0 - stepT) * T;
    }
    t += dt * (den > 0.002 ? 1.0 : 1.6);
  }
  // aerial perspective toward the horizon: fade distant clouds into haze
  float dist = sumW > 0.0 ? sumT / sumW : t0;
  // aerial perspective: distant cloud keeps its opacity but its colour fades toward the hazy horizon
  float fade = exp(-dist / 60000.0);
  vec3 haze = skyAmb * 0.55 * (1.0 - uCloudShape.z * 0.6);
  L = mix(haze * (1.0 - T), L, fade);
  // thin, sparse clouds still dissolve with distance
  T = mix(mix(1.0, T, fade), T, smoothstep(0.6, 0.9, uCloudLayer.z));
  fragColor = vec4(L, T);
}
`;

// Cloud shadow map: transmittance of sunlight through the cloud layer, top-down around the camera.
export const cloudShadowFrag = /* glsl */ `
uniform vec4 uCloudShadowXform; // x0, z0, 1/size, strength
uniform vec3 uSunDirW;
in vec2 vUv;
out vec4 fragColor;
void main() {
  float size = 1.0 / uCloudShadowXform.z;
  vec2 xz = uCloudShadowXform.xy + vUv * size;
  vec3 L = uSunDirW.y > 0.02 ? uSunDirW : vec3(0.0, 1.0, 0.0);
  float od = 0.0;
  float thick = uCloudLayer.y - uCloudLayer.x;
  const int N = 10;
  for (int i = 0; i < N; i++) {
    float h = uCloudLayer.x + thick * (float(i) + 0.5) / float(N);
    float tt = (h - 2300.0) / max(L.y, 0.08);
    vec3 p = vec3(xz.x, 0.0, xz.y) + L * tt;
    p.y = h;
    od += cloudDensity(p, (float(i) + 0.5) / float(N), false) * thick / float(N);
  }
  fragColor = vec4(vec3(exp(-od * 0.045 * 0.6)), 1.0);
}
`;
